import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Image, Button, message, Spin } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import apiClient from '../server/server';
import debounce from 'lodash/debounce';

const { Option } = Select;

const TrackSelector = ({ value = [], onChange, maxCount = 20 }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const previousValueRef = useRef(null);

  // Sync with value prop changes
  useEffect(() => {
    // Only proceed if value has actually changed
    const valueString = JSON.stringify(value);
    const previousValueString = JSON.stringify(previousValueRef.current);
    
    if (valueString === previousValueString) {
      return; // No change, don't do anything
    }
    
    console.log('🎵 [TrackSelector] Value prop changed:', value);
    previousValueRef.current = value;
    
    // Only proceed if value is an array
    if (!Array.isArray(value)) {
      return;
    }
    
    if (value.length > 0) {
      // Update selected tracks from stored data
      console.log('🎵 [TrackSelector] Updating selected tracks for IDs:', value);
      updateSelectedTracks(value);
    } else {
      // Only update if selectedTracks is not already empty
      if (selectedTracks.length > 0) {
        setSelectedTracks([]);
      }
    }
  }, [value]); // Keep value as dependency but use ref to prevent unnecessary updates

  // Store selected track data directly (no need to fetch again)
  const [selectedTrackData, setSelectedTrackData] = useState({});

  // Update selected tracks when value changes
  const updateSelectedTracks = (trackIds) => {
    if (!trackIds || trackIds.length === 0) {
      setSelectedTracks([]);
      return;
    }
    
    // Use stored track data instead of fetching
    const tracks = trackIds.map(id => selectedTrackData[id]).filter(Boolean);
    setSelectedTracks(tracks);
  };

  // Search tracks using MeiliSearch
  const searchTracks = useCallback(
    debounce(async (searchText) => {
      if (!searchText || searchText.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        setLoading(true);
        console.log('🔍 [Track Search] Searching tracks:', searchText);
        
        // Try MeiliSearch first
        try {
          const response = await apiClient.get(`/meilisearch/search/tracks`, {
            params: {
              q: searchText,
              limit: 50
            }
          });

          console.log('🔍 [Track Search] MeiliSearch Response:', response.data);
          console.log('🔍 [Track Search] First track structure:', response.data?.hits?.[0]);
          console.log('🔍 [Track Search] First track ID:', response.data?.hits?.[0]?._id || response.data?.hits?.[0]?.id);
          console.log('🔍 [Track Search] All track IDs from MeiliSearch:', response.data?.hits?.slice(0, 5).map(t => ({ id: t.id, _id: t._id, title: t.title })));
          
          if (response.data && response.data.hits && Array.isArray(response.data.hits)) {
            // Normalize MeiliSearch data to match expected structure
            const normalizedTracks = response.data.hits.map(track => ({
              _id: track.id, // Use 'id' as '_id'
              title: track.title,
              artist: track.artist,
              flyer: track.flyer || track.cover,
              ...track // Keep original data
            }));
            setSearchResults(normalizedTracks);
            console.log('🔍 [Track Search] Found', response.data.hits.length, 'tracks via MeiliSearch');
            console.log('🔍 [Track Search] Normalized tracks:', normalizedTracks.slice(0, 2));
            return;
          }
        } catch (meiliError) {
          console.error('🔍 [Track Search] MeiliSearch failed:', meiliError);
          setSearchResults([]);
        }
      } catch (error) {
        console.error('🔍 [Track Search] Error:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 600),
    []
  );

  const handleSearch = (searchText) => {
    setSearchValue(searchText);
    searchTracks(searchText);
  };

  const handleSelect = (trackId) => {
    const track = searchResults.find(t => t._id === trackId || t.id === trackId || (t.publicationData && t.publicationData._id === trackId));
    if (!track) {
      console.error('🎵 [TrackSelector] Track not found in search results:', trackId);
      console.log('🎵 [TrackSelector] Available tracks:', searchResults.map(t => ({ _id: t._id, id: t.id, publicationId: t.publicationData?._id })));
      return;
    }

    console.log('🎵 [TrackSelector] Selecting track:', track);
    console.log('🎵 [TrackSelector] Track ID sources:', { _id: track._id, id: track.id, publicationId: track.publicationData?._id });

    // Find the actual ID to use - try different possible ID fields
    const actualId = track._id || track.id || track.publicationData?._id;
    console.log('🎵 [TrackSelector] Using actual ID:', actualId);

    // Check if already selected
    if (selectedTracks.find(t => (t._id || t.id || t.publicationData?._id) === actualId)) {
      message.warning('Track already selected');
      return;
    }

    // Check max count
    if (selectedTracks.length >= maxCount) {
      message.warning(`Maximum ${maxCount} tracks allowed`);
      return;
    }

    // Normalize track data from MeiliSearch to expected format
    const trackWithId = {
      _id: actualId,
      title: track.title,
      artist: track.artist,
      flyer: track.flyer || track.cover,
      // Keep original track data
      ...track
    };

    // Store track data for later use
    setSelectedTrackData(prev => ({
      ...prev,
      [actualId]: trackWithId
    }));
    
    // Add track to selected tracks immediately
    const newSelectedTracks = [...selectedTracks, trackWithId];
    setSelectedTracks(newSelectedTracks);
    
    // Call onChange with just the IDs
    if (onChange) {
      const newIds = newSelectedTracks.map(t => t._id || t.id || t.publicationData?._id);
      console.log('🎵 [TrackSelector] Calling onChange with IDs:', newIds);
      console.log('🎵 [TrackSelector] Selected tracks structure:', newSelectedTracks.map(t => ({
        _id: t._id,
        id: t.id,
        publicationId: t.publicationData?._id,
        title: t.title
      })));
      onChange(newIds);
    }

    const trackTitle = track.publicationData?.title || track.title || 'Unknown Track';
    message.success(`Added "${trackTitle}" to music list`);

    // Clear search after a short delay to ensure the track is properly added
    setTimeout(() => {
      setSearchValue('');
      setSearchResults([]);
    }, 100);
  };

  const handleRemove = (trackId) => {
    console.log('🎵 [TrackSelector] Removing track:', trackId);
    const newSelectedTracks = selectedTracks.filter(t => t._id !== trackId);
    setSelectedTracks(newSelectedTracks);
    
    // Call onChange with just the IDs
    if (onChange) {
      const newIds = newSelectedTracks.map(t => t._id);
      console.log('🎵 [TrackSelector] Calling onChange with IDs after removal:', newIds);
      onChange(newIds);
    }
  };

  const renderTrackOption = (track) => {
    // Handle MeiliSearch results (direct fields) vs API results (nested in publicationData)
    const title = track.title || track.publicationData?.title || '[No Title]';
    const artist = track.artist || track.publicationData?.artist || '[No Artist]';
    const flyer = track.flyer || track.cover || track.publicationData?.flyer || track.publicationData?.cover;

    return (
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
        <div style={{
          width: 40,
          height: 40,
          marginRight: 12,
          borderRadius: 4,
          overflow: 'hidden',
          flexShrink: 0,
          background: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {flyer ? (
            <Image
              src={flyer}
              alt={title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              preview={false}
            />
          ) : (
            <span style={{ fontSize: 12, color: '#999' }}>♪</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 500,
            fontSize: 14,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {title}
          </div>
          <div style={{
            color: '#666',
            fontSize: 12,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {artist}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Search and Select */}
      <Select
        showSearch
        placeholder="Search tracks by title, artist, or genre..."
        style={{ width: '100%', marginBottom: 16 }}
        onSearch={handleSearch}
        onSelect={handleSelect}
        value={searchValue}
        loading={loading}
        filterOption={false}
        notFoundContent={loading ? <Spin size="small" /> : (searchValue && searchValue.length >= 2 ? 'No tracks found' : 'Type to search tracks...')}
        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
      >
        {searchResults.map((track, index) => {
          const trackId = track._id || track.id || track.publicationData?._id || `fallback-${index}`;
          return (
            <Option key={trackId} value={trackId}>
              {renderTrackOption(track)}
            </Option>
          );
        })}
      </Select>

      {/* Selected Tracks Display */}
      {selectedTracks.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500, color: '#666' }}>
            Selected Tracks ({selectedTracks.length}/{maxCount})
          </div>
          <div style={{ 
            border: '1px solid #d9d9d9', 
            borderRadius: 6, 
            padding: 8,
            maxHeight: 300,
            overflowY: 'auto'
          }}>
            {selectedTracks.map((track, index) => {
              // Handle both MeiliSearch results and API results
              const title = track.title || track.publicationData?.title || 'Untitled Track';
              const artist = track.artist || track.publicationData?.artist || 'Unknown Artist';
              const flyer = track.flyer || track.cover || track.publicationData?.flyer || track.publicationData?.cover;
              
              return (
                <div 
                  key={track._id || `selected-${index}`}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 0',
                    borderBottom: index < selectedTracks.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  <div style={{ 
                    width: 32, 
                    height: 32, 
                    marginRight: 12, 
                    borderRadius: 4,
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {flyer ? (
                      <Image
                        src={flyer}
                        alt={title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        preview={false}
                      />
                    ) : (
                      <span style={{ fontSize: 10, color: '#999' }}>♪</span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: 500, 
                      fontSize: 13,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {title}
                    </div>
                    <div style={{ 
                      color: '#666', 
                      fontSize: 11,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {artist}
                    </div>
                  </div>
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(track._id)}
                    style={{ marginLeft: 8 }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackSelector; 